import React, { useEffect, useState } from 'react';
import { Button, notification, Spin } from 'antd';
import { contracts } from '../api';
import { userRole, employeeId } from '../../GetRole.utils';
import StepDrawer from '../other/StepDrawer';
import {
  EntityChoice,
  ClientSelect,
  TermFields,
  FileDrop,
  Summary,
  entityName,
  apiErrorMessage,
  parseApiDate,
  formatDate,
} from '../other/contractFields';

const STATUS = { draft: 1, pending: 2 };

const emptyValues = (prefill) => ({
  seId: prefill?.seId ?? 1,
  clientId: prefill?.clientId ?? null,
  clientLabel: prefill?.legalEntityName ?? '',
  start: null,
  end: null,
  endless: false,
});

// Create or edit an NDA in a step drawer.
// contractId + status: edit an existing NDA. prefill: lock the client (from the client page).
const NdaDrawer = ({ open, onClose, contractId, status, prefill }) => {
  const isEdit = !!contractId;
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(emptyValues(prefill));
  const [file, setFile] = useState(null);
  const [existingFileName, setExistingFileName] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(false);

  const canSendForApproval = userRole !== 'finance' && (!isEdit || status === 'draft');
  // editing a draft: only the contracting entity is disabled, every other field stays enabled
  const isDraftEdit = isEdit && status === 'draft';
  const entityLocked = isEdit || !!prefill?.clientId;
  const clientLocked = isDraftEdit ? false : isEdit || !!prefill?.clientId;

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors({});
    setFile(null);
    setExistingFileName('');
    setValues(emptyValues(prefill));
    if (!isEdit) return;

    setLoading(true);
    contracts
      .getContractsData(contractId)
      .then((res) => {
        const data = res.data[0]?.contractData || {};
        setValues({
          seId: Number(data.seId),
          clientId: data.client?.clientId ?? null,
          clientLabel: data.client?.legalEntityName ?? '',
          start: parseApiDate(data.activationDate),
          end: parseApiDate(data.expiryDate),
          endless: data.expiryDate == null,
        });
        setExistingFileName(data.fileName || '');
      })
      .catch((err) => console.error('Error fetching NDA:', err))
      .finally(() => setLoading(false));
    // prefill is read once when the drawer opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId]);

  const update = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      if (patch.end !== undefined || patch.endless !== undefined) delete next.end;
      return next;
    });
  };

  const stepErrors = (index) => {
    const e = {};
    if (index === 0) {
      if (!values.clientId) e.clientId = 'Choose a client';
      if (!values.start) e.start = 'Choose a start date';
      if (!values.endless && !values.end) e.end = 'Choose an end date, or tick "No end date"';
    }
    if (index === 1 && !file && !existingFileName) e.file = 'Upload the signed NDA';
    return e;
  };

  const goNext = () => {
    const e = stepErrors(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  };

  const save = (cStatusId) => {
    // a draft can be saved half-filled; approval needs every step complete
    if (cStatusId === STATUS.pending) {
      for (let i = 0; i < 2; i++) {
        const e = stepErrors(i);
        if (Object.keys(e).length) {
          setErrors(e);
          setStep(i);
          return;
        }
      }
    }

    const obj = {
      seId: values.seId,
      clientId: values.clientId,
      contractTypeId: 0,
      activationDate: values.start,
      cStatusId,
      createdBy: employeeId,
    };
    if (!values.endless && values.end) obj.expiryDate = values.end;

    const formdata = new FormData();
    if (file) formdata.append('file', file);
    formdata.append('obj', JSON.stringify(obj));

    setSaving(cStatusId === STATUS.pending ? 'approval' : 'draft');
    const request = isEdit
      ? contracts.updateContractsData(contractId, formdata)
      : contracts.addContractsData(formdata);

    request
      .then((res) => {
        notification.success({
          message:
            cStatusId === STATUS.pending
              ? 'NDA sent for approval'
              : res?.message || 'NDA saved',
        });
        onClose(true);
      })
      .catch((err) => {
        notification.error({
          message: apiErrorMessage(err, 'Could not save the NDA'),
        });
      })
      .finally(() => setSaving(null));
  };

  const steps = [
    {
      key: 'agreement',
      title: 'Agreement',
      content: loading ? (
        <Spin />
      ) : (
        <div className="sd-form">
          <EntityChoice
            value={values.seId}
            onChange={(seId) => update({ seId, clientId: null, clientLabel: '' })}
            disabled={entityLocked}
          />
          <ClientSelect
            seId={values.seId}
            value={values.clientId}
            onChange={(clientId, option) => update({ clientId, clientLabel: option?.label || '' })}
            disabled={clientLocked}
            currentOption={values.clientId ? { value: values.clientId, label: values.clientLabel } : null}
            error={errors.clientId}
          />
          <TermFields
            start={values.start}
            end={values.end}
            endless={values.endless}
            onChange={update}
            errors={errors}
          />
        </div>
      ),
    },
    {
      key: 'document',
      title: 'Document',
      content: (
        <div className="sd-form">
          <FileDrop
            file={file}
            existingName={existingFileName}
            onChange={(picked) => {
              setFile(picked);
              setErrors((prev) => ({ ...prev, file: undefined }));
            }}
            error={errors.file}
            label="Signed NDA"
          />
        </div>
      ),
    },
    {
      key: 'review',
      title: 'Review',
      content: (
        <div className="sd-form">
          <Summary
            rows={[
              { label: 'Contracting entity', value: entityName(values.seId) },
              { label: 'Client', value: values.clientLabel },
              { label: 'Start date', value: formatDate(values.start) },
              {
                label: 'End date',
                value: values.endless ? 'No end date' : formatDate(values.end),
              },
              { label: 'Document', value: file?.name || existingFileName },
            ]}
          />
          {canSendForApproval && (
            <span className="sd-help">
              Sending for approval moves the NDA to Pending approval. You can also keep it as a draft.
            </span>
          )}
        </div>
      ),
    },
  ];

  const draftLabel = isEdit ? 'Save changes' : 'Save as draft';

  return (
    <StepDrawer
      open={open}
      title={isEdit ? 'Edit NDA' : 'New NDA'}
      subtitle={values.clientLabel || 'Non-disclosure agreement'}
      steps={steps}
      current={step}
      onStepClick={(index) => (index < step ? setStep(index) : goNext())}
      onBack={() => setStep(step - 1)}
      onNext={goNext}
      onClose={() => onClose(false)}
      footerStart={
        canSendForApproval && (
          <Button
            onClick={() => save(STATUS.draft)}
            loading={saving === 'draft'}
            disabled={!!saving}
          >
            {draftLabel}
          </Button>
        )
      }
      submitButton={
        canSendForApproval ? (
          <Button
            type="primary"
            onClick={() => save(STATUS.pending)}
            loading={saving === 'approval'}
            disabled={!!saving}
          >
            Send for approval
          </Button>
        ) : (
          <Button
            type="primary"
            onClick={() => save(STATUS.draft)}
            loading={saving === 'draft'}
            disabled={!!saving}
          >
            {draftLabel}
          </Button>
        )
      }
    />
  );
};

export default NdaDrawer;
